/**
 * Org Task Row Utilities
 *
 * Transforms org-level task data into "virtual crop rows" for the timeline,
 * and handles inserting those rows into the correct position within grouped
 * crop arrays based on the active grouping strategy.
 */

import { parseIntervalToDays } from '~/common/utils/date-utils'
import { WEEK_MS } from '../crop-timeline/timeline-constants'

/**
 * Transform org task groups to timeline-ready "org task row" objects.
 * Each org produces one row with all its tasks in week/day format.
 * Mirrors taskDataToWeekFormat from timeline-task-utils.js.
 *
 * @param {Array} orgTaskGroups - [{ org, tasks }]
 * @param {boolean} dayPrecision
 * @param {Date} timelineStartDate
 * @param {number} maxWeeks
 * @returns {Array} Org task row objects (virtual crop rows)
 */
export function orgTasksToWeekFormat(orgTaskGroups, dayPrecision = false, timelineStartDate = null, maxWeeks = 52) {
  if (!orgTaskGroups || orgTaskGroups.length === 0 || !timelineStartDate) {
    return []
  }

  return orgTaskGroups
    .filter(group => group.tasks && group.tasks.length > 0)
    .map(({ org, tasks }) => {
      const weekFormatTasks = tasks.map(task => {
        const taskStart = new Date(task.task_window_start)
        const durationDays = parseIntervalToDays(task.duration)

        const completed_at_weeks = (task.state === 'completed' && task.completed_at)
          ? (new Date(task.completed_at) - timelineStartDate) / (WEEK_MS)
          : null

        if (dayPrecision) {
          const daysFromStart = (taskStart - timelineStartDate) / (24 * 60 * 60 * 1000)
          const startInWeeks = daysFromStart / 7
          const lenInWeeks = durationDays / 7
          return {
            type: task.task_types?.code || task.task_code,
            start: Math.max(0, Math.min(maxWeeks, startInWeeks)),
            len: Math.max(1 / 7, lenInWeeks),
            label: task.task_types?.name || task.task_code,
            task_code: task.task_types?.code || task.task_code,
            task_window_start: task.task_window_start,
            duration: task.duration,
            notes: task.notes,
            state: task.state,
            completed_at: task.completed_at ?? null,
            completed_at_weeks,
            id: task.id,
          }
        } else {
          const weekFromStart = Math.floor((taskStart - timelineStartDate) / (WEEK_MS))
          return {
            type: task.task_types?.code || task.task_code,
            start: Math.max(0, Math.min(maxWeeks - 1, weekFromStart)),
            len: Math.max(1, Math.ceil(durationDays / 7)),
            label: task.task_types?.name || task.task_code,
            task_code: task.task_types?.code || task.task_code,
            task_window_start: task.task_window_start,
            duration: task.duration,
            notes: task.notes,
            state: task.state,
            completed_at: task.completed_at ?? null,
            completed_at_weeks,
            id: task.id,
          }
        }
      })

      return {
        id: `org-row-${org.id}`,
        organization_id: org.id,
        organization: org,
        name: org.name,
        isOrgTaskRow: true,
        tasks: weekFormatTasks,
        phases: [],
      }
    })
}

/**
 * Sort org task rows by the active sort strategy.
 *
 * @param {Array} orgTaskRows - Org task row objects
 * @param {string} sortStrategy - 'alphabetical' | 'chronological'
 * @returns {Array} Sorted copy of orgTaskRows
 */
function sortOrgTaskRows(orgTaskRows, sortStrategy) {
  if (!orgTaskRows || orgTaskRows.length <= 1) return orgTaskRows

  return [...orgTaskRows].sort((a, b) => {
    if (sortStrategy === 'chronological') {
      // Sort by earliest task start date
      const aEarliest = a.tasks.length > 0 ? a.tasks[0].start : Infinity
      const bEarliest = b.tasks.length > 0 ? b.tasks[0].start : Infinity
      return aEarliest - bEarliest
    }
    // Default: alphabetical by org name
    return (a.name || '').localeCompare(b.name || '')
  })
}

/**
 * Insert org task rows into crop groups based on the active grouping strategy.
 * Org task rows are always placed BEFORE regular crops within their target group.
 *
 * Strategy behaviour:
 * - 'none': Prepend sorted org task rows to the single ungrouped list
 * - 'location': Prepend matching org task rows to the top of each location group;
 *   orgs with tasks but no crops get their own location group prepended at the front
 * - 'species' | 'family': Create a 'None' group at the very front containing all org task rows
 *
 * @param {Array} groups - Sorted crop groups from useCropScheduleSort
 * @param {Array} orgTaskRows - Org task row objects
 * @param {string} groupStrategy - 'none' | 'location' | 'species' | 'family'
 * @param {string} sortStrategy - 'alphabetical' | 'chronological'
 * @returns {Array} Groups with org task rows inserted
 */
export function insertOrgTaskRowsIntoGroups(groups, orgTaskRows, groupStrategy, sortStrategy) {
  if (!orgTaskRows || orgTaskRows.length === 0) return groups
  if (!groups || groups.length === 0) {
    // No crop groups yet — return org task rows as a single ungrouped group
    const sorted = sortOrgTaskRows(orgTaskRows, sortStrategy)
    return [{
      groupId: 'ungrouped-all',
      groupName: null,
      groupType: null,
      crops: sorted,
      isUngrouped: true,
    }]
  }

  const sorted = sortOrgTaskRows(orgTaskRows, sortStrategy)

  switch (groupStrategy) {
    case 'location': {
      // Build a map of orgId → org task rows for this org
      const rowsByOrgId = new Map()
      sorted.forEach(row => {
        rowsByOrgId.set(row.organization_id, row)
      })

      // Prepend matching org task rows to each existing location group
      const updatedGroups = groups.map(group => {
        const matchingRow = rowsByOrgId.get(group.groupId)
        if (!matchingRow) return group
        rowsByOrgId.delete(group.groupId)  // Mark as consumed
        return {
          ...group,
          crops: [matchingRow, ...group.crops],
        }
      })

      // Any remaining org task rows have no matching crop group — create new location groups
      const newGroups = []
      rowsByOrgId.forEach((row) => {
        newGroups.push({
          groupId: row.organization_id,
          groupName: row.name,
          groupType: 'location',
          crops: [row],
          isUngrouped: false,
        })
      })

      // New location groups go at the top
      return [...newGroups, ...updatedGroups]
    }

    case 'species':
    case 'family': {
      // Create a 'None' group at the very top with all org task rows
      const noneGroup = {
        groupId: 'org-tasks-none',
        groupName: 'None',
        groupType: groupStrategy,
        crops: sorted,
        isUngrouped: false,
      }
      return [noneGroup, ...groups]
    }

    case 'none':
    default: {
      // Single ungrouped list — prepend org task rows
      if (groups.length === 0) return groups
      return [
        {
          ...groups[0],
          crops: [...sorted, ...groups[0].crops],
        },
        ...groups.slice(1),
      ]
    }
  }
}
